import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { AnchorModule } from '@epicuro-next/ataraxia/anchor';
import { IconsModule } from '@epicuro-next/ataraxia/icons';
import { IMedia, MediaType } from '@epicuro-next/ataraxia/models';
import { IBanner, ILink } from '@epicuro-next/data/merchandise';
import { isImage } from '@epicuro-next/utilities';
import { MediaModule } from 'libs/ataraxia/media/src/lib/media.module';

import { IAnchorProps, LandingLinkService } from '../service/link.service';

@Component({
  selector: 'epc-banner',
  templateUrl: './banner.component.html',
  styleUrls: ['./banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, AnchorModule, MediaModule, IconsModule],
  providers: [LandingLinkService],
})
export class BannerComponent implements OnChanges {
  @Input()
  public banner: IBanner;

  @Output()
  public linkClick = new EventEmitter<{
    url: string;
    position: number;
    link: ILink;
  }>();

  public videoPlayerProps: Partial<HTMLVideoElement> = {
    autoplay: true,
    controls: false,
    muted: true,
    volume: 1,
  };
  public mediaTypes = MediaType;

  public bannerMedia?: IMedia;
  public bannerSecondaryMedias?: IMedia[];
  public activeBannerMedia?: IMedia;

  public get firstFullScreenLink() {
    const index = this.banner?.links?.findIndex(
      (link) => link?.fullScreen === 'true',
    );
    return index > -1
      ? {
          link: this.banner?.links?.[index],
          index,
        }
      : undefined;
  }

  constructor(
    private linkService: LandingLinkService,
    private router: ActivatedRoute,
  ) {}

  public ngOnChanges(simpleChanges: SimpleChanges) {
    if (!!simpleChanges.banner.currentValue) {
      this.setMedia();
      this.setBannerSecondaryMedias();
    }
  }

  public getAnchorProps(link: ILink) {
    return this.linkService.getAnchorProps(
      link,
      this.router.snapshot.params.siteId,
    );
  }

  public toggleBannerHover(link?: ILink) {
    if (!link) {
      this.activeBannerMedia = this.bannerMedia;
      return;
    }

    this.activeBannerMedia =
      this.bannerSecondaryMedias?.find((sec) => sec.url === link.image) ??
      this.bannerMedia;
  }

  private setMedia() {
    if (this.banner.video && this.banner.video.length > 0) {
      this.bannerMedia = {
        url: this.banner.video[0].url,
        type: MediaType.VIDEO,
      };
      this.activeBannerMedia = this.bannerMedia;
      return;
    }

    this.bannerMedia = {
      url: this.banner.image ?? '',
      type: MediaType.IMAGE,
    };
    this.activeBannerMedia = this.bannerMedia;
  }

  private setBannerSecondaryMedias() {
    this.bannerSecondaryMedias = this.banner.links
      .filter((link) => !!link.image)
      .map(({ image }) => ({
        url: image as string,
        type: isImage(image ?? '') ? MediaType.IMAGE : MediaType.VIDEO,
      }));
  }

  public toggleVideoVolume() {
    this.videoPlayerProps = {
      ...this.videoPlayerProps,
      muted: !this.videoPlayerProps.muted,
    };
  }

  public onLinkClick(routeProps: IAnchorProps, position: number, link: ILink) {
    this.linkClick.emit({
      url: this.linkService.anchorPropsToHref(routeProps),
      position: position + 1,
      link,
    });
  }
}
